import { RING } from '../components/wall/wallTokens';
import { timeLabel } from './surfaceFormat';

/**
 * A Portal's layout: where the words, the event and the venue sit on the card, how big the
 * words are, which font and highlight they wear, how the event and the venue show, how each
 * element is turned and scaled, and (text-only) which animated background plays.
 * Handoff: docs/plans/2026-09-portal-composer-v3/handoff/README.md §5 and §9.
 *
 * Positions are a percent of the card so the same layout lands on a 390px phone and a 400px
 * desktop card. The size is relative to a 400px-wide card; readers scale it by width / 400.
 * The picture's own pan and zoom live on the story's `frame` (utils/mediaFrame); only its
 * rotation is here, because the frame is drawn by every surface and rotation is new.
 */

export const SIZE_MIN = 18;
export const SIZE_MAX = 72;
export const SIZE_DEFAULT = 34;
/** The words are 140 characters at most. */
export const TEXT_MAX = 140;
export const TEXT_DEFAULT = Object.freeze({ x: 6, y: 20 });
export const EVENT_DEFAULT = Object.freeze({ x: 6, y: 54 });
export const VENUE_DEFAULT = Object.freeze({ x: 6, y: 66 });
/** The bottom 13% of the card is the guests' action row; nothing is placed past this line. */
export const SAFE_TOP = 87;
/** Nothing is placed above this line — the header lives there. */
export const TOP_LIMIT = 11;
/** The words block's width, in percent of the card. */
export const BLOCK_WIDTH = 76;
export const ROT_MIN = -180;
export const ROT_MAX = 180;
/** An event or a venue can be pinched between these. */
export const ELEMENT_SCALE_MIN = 0.6;
export const ELEMENT_SCALE_MAX = 1.8;
/** The picture can be pinched between these (× 100 on the frame). */
export const MEDIA_SCALE_MIN = 1;
export const MEDIA_SCALE_MAX = 4;
/** Rotation snaps to level inside this. */
export const ROT_SNAP = 4;

export const FONTS = [
  { key: 'serif', label: 'Serif', family: "'Times New Roman', Times, serif", style: 'italic', weight: 400, transform: 'none', tracking: '0', display: false },
  { key: 'mono', label: 'Mono', family: "'JetBrains Mono', Menlo, monospace", style: 'normal', weight: 700, transform: 'none', tracking: '-0.01em', display: false },
  { key: 'sans', label: 'Sans', family: 'Arial, Helvetica, sans-serif', style: 'normal', weight: 700, transform: 'none', tracking: '-0.01em', display: false },
  { key: 'caps', label: 'CAPS', family: "'JetBrains Mono', Menlo, monospace", style: 'normal', weight: 700, transform: 'uppercase', tracking: '0.08em', display: false },
  { key: 'disco', label: 'DISCO', family: "'Monoton', cursive", style: 'normal', weight: 400, transform: 'uppercase', tracking: '0.04em', display: true, google: 'Monoton' },
  { key: 'marker', label: 'Marker', family: "'Permanent Marker', cursive", style: 'normal', weight: 400, transform: 'none', tracking: '0', display: true, google: 'Permanent+Marker' },
  { key: 'bubble', label: 'Bubble', family: "'Bagel Fat One', cursive", style: 'normal', weight: 400, transform: 'none', tracking: '0', display: true, google: 'Bagel+Fat+One' },
  { key: 'glitch', label: 'GLITCH', family: "'Rubik Glitch', sans-serif", style: 'normal', weight: 400, transform: 'uppercase', tracking: '0', display: true, google: 'Rubik+Glitch' },
  { key: 'brush', label: 'Brush', family: "'Caveat Brush', cursive", style: 'normal', weight: 400, transform: 'none', tracking: '0', display: true, google: 'Caveat+Brush' },
  { key: 'poster', label: 'POSTER', family: "'Bebas Neue', Impact, sans-serif", style: 'normal', weight: 400, transform: 'uppercase', tracking: '0.03em', display: true, google: 'Bebas+Neue' },
  { key: 'pixel', label: 'PIXEL', family: "'Press Start 2P', monospace", style: 'normal', weight: 400, transform: 'uppercase', tracking: '0', display: true, google: 'Press+Start+2P' },
  { key: 'crt', label: 'CRT', family: "'VT323', monospace", style: 'normal', weight: 400, transform: 'none', tracking: '0.02em', display: true, google: 'VT323' },
  { key: 'retro', label: 'Retro', family: "'Shrikhand', cursive", style: 'normal', weight: 400, transform: 'none', tracking: '0', display: true, google: 'Shrikhand' },
  { key: 'fatface', label: 'Fatface', family: "'Abril Fatface', serif", style: 'normal', weight: 400, transform: 'none', tracking: '0.01em', display: true, google: 'Abril+Fatface' },
  { key: 'neon', label: 'Neon', family: "'Pacifico', cursive", style: 'normal', weight: 400, transform: 'none', tracking: '0', display: true, google: 'Pacifico' },
  { key: 'major', label: 'mAjor', family: "'Major Mono Display', monospace", style: 'normal', weight: 400, transform: 'none', tracking: '0', display: true, google: 'Major+Mono+Display' },
  { key: 'editorial', label: 'Editorial', family: "'Playfair Display', serif", style: 'italic', weight: 800, transform: 'none', tracking: '-0.01em', display: true, google: 'Playfair+Display:ital,wght@1,800' },
];

export const HIGHLIGHTS = [
  { key: 'black', label: 'BLACK', bg: '#000', fg: '#fff', shadow: 'none', animated: false },
  { key: 'ring', label: 'PORTAL', bg: RING, fg: '#000', shadow: 'none', animated: true },
  { key: 'white', label: 'WHITE', bg: '#fff', fg: '#000', shadow: 'none', animated: false },
  { key: 'none', label: 'NONE', bg: 'transparent', fg: '#fff', shadow: '0 2px 12px rgba(0,0,0,.7), 0 1px 2px rgba(0,0,0,.8)', animated: false },
];

/** The eleven ways an event or a venue can show. Same keys for both (handoff §5.4). */
export const ELEMENT_STYLES = [
  { key: 'card', label: 'CARD' },
  { key: 'mini', label: 'MINI' },
  { key: 'stub', label: 'STUB' },
  { key: 'tag', label: 'TAG' },
  { key: 'date', label: 'DATE' },
  { key: 'pin', label: 'PIN' },
  { key: 'ticket', label: 'TICKET' },
  { key: 'neon', label: 'NEON' },
  { key: 'poster', label: 'POSTER' },
  { key: 'sticker', label: 'STICKER' },
  { key: 'serif', label: 'SCRIPT' },
];
export const VENUE_STYLES = ELEMENT_STYLES;
export const EVENT_STYLES = ELEMENT_STYLES;

/**
 * In list order. The first six are the party pack (a badge on the swatch); five of them are
 * WebGL shaders and STAGE is the DOM recipe. TUNNEL is a shader too, without the badge. Lasers
 * is the default.
 */
export const BACKGROUNDS = [
  { key: 'lasers', label: 'LASERS', shader: true, party: true, swatch: 'repeating-conic-gradient(from 150deg at 50% 105%, #FF33C6 0 2deg, transparent 2deg 9deg, #22ECFB 9deg 11deg, transparent 11deg 18deg), #0a0212' },
  { key: 'stage', label: 'STAGE', swatch: 'linear-gradient(160deg, #000 30%, #FD199F 60%, #127BFD 85%, #000)' },
  { key: 'fractal', label: 'FRACTAL', shader: true, party: true, swatch: 'radial-gradient(circle, #000 12%, transparent 13%), conic-gradient(from 20deg, #FD199F, #B5B1FF, #127BFD, #22ECFB, #FAE1B4, #FCAA6D, #FD199F)' },
  { key: 'liquid', label: 'LIQUID', shader: true, party: true, swatch: 'radial-gradient(circle at 30% 30%, #22ECFB, transparent 50%), radial-gradient(circle at 72% 70%, #FD199F, transparent 55%), radial-gradient(circle at 70% 20%, #FAE1B4, transparent 40%), #5b3fb0' },
  { key: 'mirror', label: 'MIRROR', shader: true, party: true, swatch: 'radial-gradient(#fff 1.2px, transparent 1.8px) 0 0 / 7px 7px, radial-gradient(circle at 50% 10%, #5a2070, #000 70%)' },
  { key: 'warp', label: 'WARP', shader: true, party: true, swatch: 'radial-gradient(circle, #fff 0 6%, transparent 7%), repeating-radial-gradient(circle, #FD199F 0 3px, #127BFD 3px 6px, #22ECFB 6px 9px, #000 9px 12px)' },
  { key: 'haze', label: 'HAZE', swatch: 'radial-gradient(circle at 30% 30%, rgba(181,177,255,.45), #000 70%)' },
  { key: 'tunnel', label: 'TUNNEL', shader: true, swatch: 'repeating-radial-gradient(circle at 50% 50%, #000 0 3px, #B5B1FF 3px 5px, #000 5px 8px, #22ECFB 8px 10px)' },
  { key: 'dusk', label: 'DUSK', swatch: 'linear-gradient(#000, #1c0a26 70%, #4a2a22)' },
  { key: 'aurora', label: 'AURORA', swatch: 'radial-gradient(circle at 25% 30%, #FD199F, transparent 55%), radial-gradient(circle at 75% 70%, #22ECFB, transparent 55%), #127BFD' },
  { key: 'fleck', label: 'FLECK', swatch: 'radial-gradient(#FF33C6 1.5px, transparent 2px) 0 0 / 10px 9px, radial-gradient(#22ECFB 1.5px, transparent 2px) 5px 4px / 10px 9px, #000' },
  { key: 'grid', label: 'GRID', swatch: 'linear-gradient(#3a0a4a 45%, #FF33C6 46%, #000 48%)' },
  { key: 'signal', label: 'SIGNAL', swatch: 'linear-gradient(#000 20%, #22ECFB 20% 28%, #000 28% 48%, #FD199F 48% 58%, #000 58% 76%, #FAE1B4 76% 82%, #000 82%)' },
  { key: 'ink', label: 'INK', swatch: 'linear-gradient(#0B0B0B 80%, #FF33C6 80% 88%, #22ECFB 88%)' },
];
/** Sent before v3 and still drawn, but no longer offered. */
export const LEGACY_BACKGROUNDS = ['pulse'];

export const ROT_DEFAULT = Object.freeze({ text: 0, event: 0, venue: 0, media: 0 });
export const SCL_DEFAULT = Object.freeze({ event: 1, venue: 1 });

export const DEFAULT_LAYOUT = Object.freeze({
  text: TEXT_DEFAULT,
  event: EVENT_DEFAULT,
  venue: VENUE_DEFAULT,
  size: SIZE_DEFAULT,
  font: 'serif',
  highlight: 'black',
  eventStyle: 'card',
  venueStyle: 'card',
  background: 'lasers',
  rot: ROT_DEFAULT,
  scl: SCL_DEFAULT,
});

const has = (list, key) => list.some((o) => o.key === key);
const num = (v, lo, hi, fallback) => (Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : fallback);
const point = (p, d) => ({ x: num(p?.x, 0, 98, d.x), y: num(p?.y, 0, 90, d.y) });

/** Wrap into −180…180 and snap to level near it. */
export function snapRotation(r) {
  if (!Number.isFinite(r)) return 0;
  const w = ((((r + 180) % 360) + 360) % 360) - 180;
  return Math.abs(w) < ROT_SNAP ? 0 : Math.round(w * 10) / 10;
}

/** A complete layout with every field valid, from whatever the server or a draft holds. */
export function normalizeLayout(raw) {
  const r = raw || {};
  const rot = r.rot || {};
  const scl = r.scl || {};
  const bg = has(BACKGROUNDS, r.background) || LEGACY_BACKGROUNDS.includes(r.background) ? r.background : 'lasers';
  return {
    text: point(r.text, TEXT_DEFAULT),
    event: point(r.event, EVENT_DEFAULT),
    venue: point(r.venue, VENUE_DEFAULT),
    size: Math.round(num(r.size, SIZE_MIN, SIZE_MAX, SIZE_DEFAULT)),
    font: has(FONTS, r.font) ? r.font : 'serif',
    highlight: has(HIGHLIGHTS, r.highlight) ? r.highlight : 'black',
    eventStyle: has(ELEMENT_STYLES, r.eventStyle) ? r.eventStyle : 'card',
    venueStyle: has(ELEMENT_STYLES, r.venueStyle) ? r.venueStyle : 'card',
    background: bg,
    rot: {
      text: num(rot.text, ROT_MIN, ROT_MAX, 0),
      event: num(rot.event, ROT_MIN, ROT_MAX, 0),
      venue: num(rot.venue, ROT_MIN, ROT_MAX, 0),
      media: num(rot.media, ROT_MIN, ROT_MAX, 0),
    },
    scl: {
      event: num(scl.event, ELEMENT_SCALE_MIN, ELEMENT_SCALE_MAX, 1),
      venue: num(scl.venue, ELEMENT_SCALE_MIN, ELEMENT_SCALE_MAX, 1),
    },
  };
}

/**
 * What goes to the server: two decimals, no event or venue position without one, and no
 * background on a Portal with a picture.
 */
export function layoutForPublish(layout, { textOnly = true, hasVenue = true, hasEvent = true } = {}) {
  const l = normalizeLayout(layout);
  const r2 = (v) => Math.round(v * 100) / 100;
  const pt = (p) => ({ x: r2(p.x), y: r2(p.y) });
  return {
    text: pt(l.text),
    event: hasEvent ? pt(l.event) : null,
    venue: hasVenue ? pt(l.venue) : null,
    size: l.size,
    font: l.font,
    highlight: l.highlight,
    eventStyle: l.eventStyle,
    venueStyle: l.venueStyle,
    background: textOnly ? l.background : null,
    rot: { text: r2(l.rot.text), event: r2(l.rot.event), venue: r2(l.rot.venue), media: r2(l.rot.media) },
    scl: { event: r2(l.scl.event), venue: r2(l.scl.venue) },
  };
}

export const fontOf = (key) => FONTS.find((f) => f.key === key) || FONTS[0];
export const highlightOf = (key) => HIGHLIGHTS.find((h) => h.key === key) || HIGHLIGHTS[0];
export const backgroundOf = (key) => BACKGROUNDS.find((b) => b.key === key) || BACKGROUNDS[0];
export const elementStyleOf = (key) => ELEMENT_STYLES.find((s) => s.key === key) || ELEMENT_STYLES[0];
export const isDisplayFont = (key) => !!fontOf(key).display;

/** The Google faces a card needs: its font, and Bebas Neue for a POSTER event or venue. */
export function googleFamiliesFor(layout, { hasEvent = false, hasVenue = false } = {}) {
  const out = new Set();
  const f = fontOf(layout?.font);
  if (f.google) out.add(f.google);
  if ((hasEvent && layout?.eventStyle === 'poster') || (hasVenue && layout?.venueStyle === 'poster')) out.add('Bebas+Neue');
  return [...out];
}

/** The window label under the words follows the size: `max(10, round(S × 0.32))`. */
export const chipSize = (size) => Math.max(10, Math.round(size * 0.32));

/** The next (or previous) key in a list, wrapping. */
export function cycle(list, key, dir = 1) {
  const i = Math.max(0, list.findIndex((o) => o.key === key));
  return list[(i + dir + list.length) % list.length].key;
}

/** The composer's chip: `NOW → 4H` without a window, else the window the way the viewer prints it. */
export function composerChip(windowOn, startIso, endIso) {
  return windowOn ? windowChip(startIso, endIso) : 'NOW → 4H';
}

/** A live Portal's chip: `NOW → 11:00 PM` once it has started, `8:00 PM → 11:00 PM` before. */
export function windowChip(startIso, endIso, now = Date.now()) {
  const end = endIso ? timeLabel(endIso) : '';
  if (!end) return '';
  const start = startIso ? new Date(startIso).getTime() : NaN;
  const later = Number.isFinite(start) && start > now + 60000;
  return `${later ? timeLabel(startIso) : 'NOW'} → ${end}`;
}

/** The size that still fits: for a block that overflows even at the top line, step down. */
export const shrink = (size) => Math.max(SIZE_MIN, size - 1);
